"use client";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { GroupedData, FlatListItem, InsertState, ConditionalInsertOptions } from "./types";

/**
 * 处理分组数据的 Hook
 * 负责数据扁平化、插入、去重等逻辑
 */
export const useGroupedData = <T>(
  data: GroupedData<T>,
  getItemKey: (item: T) => string,
  checkDuplicateGroups: number = 3,
  onDuplicateRemoved?: (item: T, groupKey: string) => void,
) => {
  // 管理内部数据状态
  const [internalData, setInternalData] = useState<GroupedData<T>>(data);

  // 插入状态管理
  const [insertState, setInsertState] = useState<InsertState>({
    insertedKeys: new Set(),
    insertTimestamps: new Map(),
  });

  // 用于跟踪插入动画完成
  const animationTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // 更新内部数据当外部数据变化时
  useEffect(() => {
    setInternalData(data);
  }, [data]);

  /**
   * 扁平化分组数据为单一列表
   * 包含分组标题和数据项
   */
  const flattenedData = useMemo<FlatListItem<T>[]>(() => {
    const result: FlatListItem<T>[] = [];
    const groupKeys = Object.keys(internalData);

    groupKeys.forEach((groupKey) => {
      const items = internalData[groupKey];

      // 添加分组标题
      result.push({
        type: "header",
        groupKey,
      });

      // 添加该分组下的所有数据项
      items.forEach((item, index) => {
        result.push({
          type: "item",
          groupKey,
          data: item,
          itemKey: getItemKey(item),
          index,
        });
      });
    });

    return result;
  }, [internalData, getItemKey]);

  /**
   * 检查并移除重复项
   * 只检查前 N 个分组
   */
  const removeDuplicates = useCallback(
    (newItems: T[]): void => {
      const newItemKeys = new Set(newItems.map(getItemKey));
      const groupKeys = Object.keys(internalData).slice(
        0,
        checkDuplicateGroups,
      );

      setInternalData((prevData) => {
        const updatedData = { ...prevData };
        let hasChanges = false;

        groupKeys.forEach((groupKey) => {
          const items = updatedData[groupKey];
          const filteredItems = items.filter((item) => {
            const itemKey = getItemKey(item);
            if (newItemKeys.has(itemKey)) {
              // 找到重复项，触发回调
              onDuplicateRemoved?.(item, groupKey);
              hasChanges = true;
              return false;
            }
            return true;
          });

          if (filteredItems.length !== items.length) {
            updatedData[groupKey] = filteredItems;
          }
        });

        return hasChanges ? updatedData : prevData;
      });
    },
    [internalData, getItemKey, checkDuplicateGroups, onDuplicateRemoved],
  );

  /**
   * 插入单个数据项到第一个分组顶部或指定位置
   */
  const insertItem = useCallback(
    (item: T, options?: ConditionalInsertOptions<T>): void => {
      const itemKey = getItemKey(item);

      // 先移除可能存在的重复项
      removeDuplicates([item]);

      // 插入到指定位置
      setInternalData((prevData) => {
        const groupKeys = Object.keys(prevData);
        if (groupKeys.length === 0) {
          // 如果没有分组，创建一个默认分组
          return { default: [item] };
        }

        // 如果没有提供选项或没有匹配器，使用默认行为（插入到第一个分组顶部）
        if (!options || !options.matcher) {
          const firstGroupKey = groupKeys[0];
          const firstGroup = prevData[firstGroupKey];

          return {
            ...prevData,
            [firstGroupKey]: [item, ...firstGroup],
          };
        }

        // 使用条件插入逻辑
        const { matcher, position = 'after', searchGroups = 1 } = options;
        const newData = { ...prevData };
        let inserted = false;

        // 限制搜索的分组数量
        const searchableGroups = groupKeys.slice(0, searchGroups);

        for (let groupIndex = 0; groupIndex < searchableGroups.length; groupIndex++) {
          const groupKey = searchableGroups[groupIndex];
          const groupItems = newData[groupKey];
          
          // 在当前分组中查找匹配项
          for (let i = 0; i < groupItems.length; i++) {
            if (matcher(groupItems[i], i, groupKey, groupIndex)) {
              // 找到匹配项，根据 position 插入
              const newGroupItems = [...groupItems];
              
              if (position === 'after') {
                newGroupItems.splice(i + 1, 0, item);
              } else if (position === 'before') {
                newGroupItems.splice(i, 0, item);
              }
              
              newData[groupKey] = newGroupItems;
              inserted = true;
              break;
            }
          }
          
          if (inserted) {
            break;
          }
        }

        // 如果没有找到匹配项，回退到默认行为（插入到第一个分组顶部）
        if (!inserted) {
          const firstGroupKey = groupKeys[0];
          const firstGroup = prevData[firstGroupKey];
          newData[firstGroupKey] = [item, ...firstGroup];
        }

        return newData;
      });

      // 标记为新插入的项
      setInsertState((prev) => ({
        insertedKeys: new Set(prev.insertedKeys).add(itemKey),
        insertTimestamps: new Map(prev.insertTimestamps).set(
          itemKey,
          Date.now(),
        ),
      }));

      // 清除之前的定时器
      const existingTimer = animationTimers.current.get(itemKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // 设置新的定时器，动画完成后移除插入标记
      const timer = setTimeout(() => {
        setInsertState((prev) => {
          const newInsertedKeys = new Set(prev.insertedKeys);
          const newTimestamps = new Map(prev.insertTimestamps);
          newInsertedKeys.delete(itemKey);
          newTimestamps.delete(itemKey);
          return {
            insertedKeys: newInsertedKeys,
            insertTimestamps: newTimestamps,
          };
        });
        animationTimers.current.delete(itemKey);
      }, 500); // 动画持续时间

      animationTimers.current.set(itemKey, timer);
    },
    [getItemKey, removeDuplicates],
  );

  /**
   * 插入多个数据项到第一个分组顶部或指定位置
   */
  const insertItems = useCallback(
    (items: T[], options?: ConditionalInsertOptions<T>): void => {
      if (items.length === 0) {
        return;
      }

      const itemKeys = items.map(getItemKey);

      // 先移除可能存在的重复项
      removeDuplicates(items);

      // 插入到指定位置
      setInternalData((prevData) => {
        const groupKeys = Object.keys(prevData);
        if (groupKeys.length === 0) {
          // 如果没有分组，创建一个默认分组
          return { default: items };
        }

        // 如果没有提供选项或没有匹配器，使用默认行为（插入到第一个分组顶部）
        if (!options || !options.matcher) {
          const firstGroupKey = groupKeys[0];
          const firstGroup = prevData[firstGroupKey];

          return {
            ...prevData,
            [firstGroupKey]: [...items, ...firstGroup],
          };
        }

        // 使用条件插入逻辑
        const { matcher, position = 'after', searchGroups = 1 } = options;
        const newData = { ...prevData };
        let inserted = false;

        // 限制搜索的分组数量
        const searchableGroups = groupKeys.slice(0, searchGroups);

        for (let groupIndex = 0; groupIndex < searchableGroups.length; groupIndex++) {
          const groupKey = searchableGroups[groupIndex];
          const groupItems = newData[groupKey];
          
          // 在当前分组中查找匹配项
          for (let i = 0; i < groupItems.length; i++) {
            if (matcher(groupItems[i], i, groupKey, groupIndex)) {
              // 找到匹配项，根据 position 插入
              const newGroupItems = [...groupItems];
              
              if (position === 'after') {
                newGroupItems.splice(i + 1, 0, ...items);
              } else if (position === 'before') {
                newGroupItems.splice(i, 0, ...items);
              }
              
              newData[groupKey] = newGroupItems;
              inserted = true;
              break;
            }
          }
          
          if (inserted) {
            break;
          }
        }

        // 如果没有找到匹配项，回退到默认行为（插入到第一个分组顶部）
        if (!inserted) {
          const firstGroupKey = groupKeys[0];
          const firstGroup = prevData[firstGroupKey];
          newData[firstGroupKey] = [...items, ...firstGroup];
        }

        return newData;
      });

      // 标记为新插入的项
      const now = Date.now();
      setInsertState((prev) => {
        const newInsertedKeys = new Set(prev.insertedKeys);
        const newTimestamps = new Map(prev.insertTimestamps);

        itemKeys.forEach((key) => {
          newInsertedKeys.add(key);
          newTimestamps.set(key, now);
        });

        return {
          insertedKeys: newInsertedKeys,
          insertTimestamps: newTimestamps,
        };
      });

      // 清除之前的定时器并设置新的
      itemKeys.forEach((itemKey) => {
        const existingTimer = animationTimers.current.get(itemKey);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        const timer = setTimeout(() => {
          setInsertState((prev) => {
            const newInsertedKeys = new Set(prev.insertedKeys);
            const newTimestamps = new Map(prev.insertTimestamps);
            newInsertedKeys.delete(itemKey);
            newTimestamps.delete(itemKey);
            return {
              insertedKeys: newInsertedKeys,
              insertTimestamps: newTimestamps,
            };
          });
          animationTimers.current.delete(itemKey);
        }, 500);

        animationTimers.current.set(itemKey, timer);
      });
    },
    [getItemKey, removeDuplicates],
  );

  /**
   * 检查某个项是否是新插入的
   */
  const isItemInserted = useCallback(
    (itemKey: string): boolean => {
      return insertState.insertedKeys.has(itemKey);
    },
    [insertState.insertedKeys],
  );

  /**
   * 获取某个分组的所有数据项
   */
  const getGroupItems = useCallback(
    (groupKey: string): T[] => {
      return internalData[groupKey] || [];
    },
    [internalData],
  );

  /**
   * 更新指定项的数据
   * 用于处理流式内容等需要更新 item 内容的场景
   */
  const updateItem = useCallback(
    (itemKey: string, updater: (item: T) => T): void => {
      setInternalData((prevData) => {
        const newData = { ...prevData };
        let found = false;

        // 在所有分组中查找并更新对应的 item
        for (const groupKey of Object.keys(newData)) {
          const groupItems = newData[groupKey];
          const itemIndex = groupItems.findIndex((item) => getItemKey(item) === itemKey);

          if (itemIndex !== -1) {
            // 找到了，更新这个 item
            const newGroupItems = [...groupItems];
            newGroupItems[itemIndex] = updater(groupItems[itemIndex]);
            newData[groupKey] = newGroupItems;
            found = true;
            break;
          }
        }

        return found ? newData : prevData;
      });
    },
    [getItemKey],
  );

  return {
    internalData,
    flattenedData,
    insertItem,
    insertItems,
    isItemInserted,
    getGroupItems,
    updateItem,
  };
};
